import { faArrowRotateRight, faUndo, faExpand, faCompress, faCheck, faXmark, faSearchPlus, faCamera } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, CircularProgress, Typography } from "@mui/material";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useGaiaMapContext } from "../hooks/useGaiaMapContext";
import {
    PanDrawButton,
    PointDrawButton,
    PolylineDrawButton,
    PolygonDrawButton,
    SubtractDrawButton,
    DeleteDrawButton,
    CloneDrawButton,
    CircleDrawButton,
    RulerDrawButton,
    SelectDrawButton,
    DeselectDrawButton,
    InspectDrawButton,
    EditDrawButton,
    LabelsDrawButton,
} from "./GaiaMapControls";
import { GAIA_DRAW_MODE, EnabledToolTypes, GaiaMapProps } from "../GaiaMap";
import useAlertService from "@mirage/hooks/useAlertService";

interface ToolItem {
    Component: React.ComponentType;
    label: string;
    mode: typeof GAIA_DRAW_MODE[keyof typeof GAIA_DRAW_MODE];
    toolType: EnabledToolTypes;
}

type GaiaMapRibbonProps = Pick<GaiaMapProps, "customRibbonButtons"> & {
    showSnapshotButton?: boolean;
};

export const GaiaMapRibbon = memo((props: GaiaMapRibbonProps) => {
    const { customRibbonButtons, showSnapshotButton = true } = props;
    const {
        canUndo,
        canRedo,
        onUndo,
        onRedo,
        drawMode,
        setDrawMode,
        acceptDrawing,
        cancelDrawing,
        hasGeometry,
        hasLabels,
        acceptLabels,
        cancelLabels,
        onClone,
        center,
        centerMap,
        readOnly,
        enabledToolTypes,
        disableUndoRedo,
        disableAcceptDrawing,
        getFeatureInfo,
        wmsUrl,
        activatePrintPreview,
        printLoading,
        onMapBoxSizeRequested,
        mapBoxSize,
        isFullScreen,
        setIsFullScreen,
        startPrintLoading,
        stopPrintLoading,
    } = useGaiaMapContext();
    const isDrawing = drawMode !== null;
    const hasCloneFunction = typeof onClone === "function";
    const showAlert = useAlertService();
    const isLabelsMode = drawMode === GAIA_DRAW_MODE.LABELS;
    const acceptDrawingMode = [GAIA_DRAW_MODE.POLYGON, GAIA_DRAW_MODE.POLYLINE, GAIA_DRAW_MODE.EDIT];
    const showAcceptDrawing = isDrawing && !disableAcceptDrawing && acceptDrawingMode.includes(drawMode as any);
    const showAcceptLabels = isLabelsMode;
    const toggleFullScreen = useCallback(() => {
        const mapContainer = document.querySelector(".gaia__container");

        if (!isFullScreen) {
            if (mapContainer && mapContainer.requestFullscreen) {
                mapContainer.requestFullscreen().catch((err) => {
                    console.warn(`Error attempting to enable fullscreen: ${err.message}`);
                });
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen().catch((err) => {
                    console.warn(`Error attempting to exit fullscreen: ${err.message}`);
                });
            }
        }

        setIsFullScreen(!isFullScreen);
    }, [isFullScreen, setIsFullScreen]);

    const takeSnapshot = useCallback(async () => {
        if (!activatePrintPreview) {
            console.log("Print Preview not available");
            showAlert("Print Preview not available", "error");
            return;
        }
        if (mapBoxSize == null && typeof onMapBoxSizeRequested === "function") {
            startPrintLoading();
            try {
                const mapBoxSize = await onMapBoxSizeRequested();
                if (!mapBoxSize) {
                    console.log("Map box size not available");
                    showAlert("Map box size not available", "error");
                    return;
                }
            } finally {
                stopPrintLoading();
            }
        }
        activatePrintPreview();
    }, [activatePrintPreview, mapBoxSize, onMapBoxSizeRequested, showAlert, startPrintLoading, stopPrintLoading]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
        };
    }, [setIsFullScreen]);

    const visibleTools = useMemo(() => {
        const allTools: (ToolItem | null)[] = [
            hasCloneFunction ? { Component: CloneDrawButton, label: "Clone", mode: GAIA_DRAW_MODE.CLONE, toolType: "polygon" } : null,
            { Component: PointDrawButton, label: "Point", mode: GAIA_DRAW_MODE.MARKER, toolType: "point" },
            { Component: PolylineDrawButton, label: "Polyline", mode: GAIA_DRAW_MODE.POLYLINE, toolType: "polyline" },
            { Component: PolygonDrawButton, label: "Polygon", mode: GAIA_DRAW_MODE.POLYGON, toolType: "polygon" },
            { Component: SubtractDrawButton, label: "Subtract", mode: GAIA_DRAW_MODE.SUBTRACT, toolType: "polygon" },
            { Component: CircleDrawButton, label: "Circle", mode: GAIA_DRAW_MODE.CIRCLE, toolType: "circle" },
            { Component: DeleteDrawButton, label: "Delete", mode: GAIA_DRAW_MODE.DELETE, toolType: "delete" },
            { Component: SelectDrawButton, label: "Select", mode: GAIA_DRAW_MODE.SELECT, toolType: "selection" },
            { Component: DeselectDrawButton, label: "Deselect", mode: GAIA_DRAW_MODE.DESELECT, toolType: "selection" },
            { Component: EditDrawButton, label: "Edit", mode: GAIA_DRAW_MODE.EDIT, toolType: "edit" },
            hasLabels ? { Component: LabelsDrawButton, label: "Labels", mode: GAIA_DRAW_MODE.LABELS, toolType: "labels" } : null,
        ];

        if (!enabledToolTypes || enabledToolTypes.length === 0) {
            if (hasLabels) {
                return allTools.filter((tool): tool is ToolItem => tool !== null && tool.mode === GAIA_DRAW_MODE.LABELS);
            }
            return [];
        }

        return allTools.filter(
            (tool): tool is ToolItem =>
                tool !== null &&
                (enabledToolTypes.includes(tool.toolType) ||
                    (tool.mode === GAIA_DRAW_MODE.CLONE && enabledToolTypes.includes("polygon") && hasCloneFunction) ||
                    (tool.mode === GAIA_DRAW_MODE.LABELS && hasLabels))
        );
    }, [enabledToolTypes, hasCloneFunction, hasLabels]);
    const rightButtons = useMemo(() => {
        const undoButton = { icon: faUndo, label: "Undo", onClick: onUndo, disabled: readOnly || !canUndo || isLabelsMode, loading: false };
        const redoButton = { icon: faArrowRotateRight, label: "Redo", onClick: onRedo, disabled: readOnly || !canRedo || isLabelsMode, loading: false };
        const snapshotButton = { icon: faCamera, label: "Take Snapshot", onClick: takeSnapshot, disabled: isLabelsMode, loading: printLoading };
        const fullScreenButton = { icon: isFullScreen ? faCompress : faExpand, label: "Full Screen", onClick: toggleFullScreen, disabled: isLabelsMode, loading: false };
        const buttons = [fullScreenButton];
        if (showSnapshotButton) {
            buttons.unshift(snapshotButton);
        }
        if (!disableUndoRedo && !readOnly && drawMode !== GAIA_DRAW_MODE.EDIT) {
            buttons.unshift(undoButton, redoButton);
        }
        return buttons;
    }, [onUndo, readOnly, canUndo, onRedo, canRedo, takeSnapshot, printLoading, isFullScreen, toggleFullScreen, showSnapshotButton, disableUndoRedo, isLabelsMode, drawMode]);
    const acceptDrawingButtons = useMemo(() => {
        return [
            { icon: faCheck, label: "Accept", onClick: acceptDrawing, disabled: !hasGeometry },
            { icon: faXmark, label: "Cancel", onClick: cancelDrawing, disabled: !hasGeometry },
        ];
    }, [acceptDrawing, cancelDrawing, hasGeometry]);

    const acceptLabelsButtons = useMemo(() => {
        return [
            { icon: faCheck, label: "Accept", onClick: acceptLabels, disabled: false },
            { icon: faXmark, label: "Cancel", onClick: cancelLabels, disabled: false },
        ];
    }, [acceptLabels, cancelLabels]);

    return (
        <Box
            sx={{
                minHeight: "60px",
                height: "80px",
                borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
                borderRight: "1px solid rgba(0, 0, 0, 0.12)",
            }}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
        >
            <Box display="flex" gap={2} alignItems="center" padding={1}>
                <Box
                    className={`gaia-draw-control ${drawMode === null ? "selected" : ""} ${isLabelsMode ? "disabled" : ""}`}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    onClick={() => {
                        if (isLabelsMode) {
                            return;
                        }
                        if (drawMode !== null && hasGeometry) {
                            cancelDrawing();
                        }
                        setDrawMode(null);
                    }}
                >
                    <PanDrawButton />
                    <Typography variant="caption">Pan</Typography>
                </Box>
                <Box
                    className={`gaia-draw-control ${isLabelsMode ? "disabled" : ""}`}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    onClick={() => {
                        if (isLabelsMode) {
                            return;
                        }
                        if (hasGeometry) {
                            centerMap(null);
                        } else {
                            centerMap(center);
                        }
                    }}
                >
                    <button className="gaia-draw-button" disabled={isLabelsMode}>
                        <FontAwesomeIcon icon={faSearchPlus} />
                    </button>
                    <Typography variant="caption">Zoom To</Typography>
                </Box>
                {getFeatureInfo && wmsUrl && (
                    <Box
                        className={`gaia-draw-control ${drawMode === GAIA_DRAW_MODE.INSPECT ? "selected" : ""} ${isLabelsMode ? "disabled" : ""}`}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        onClick={() => {
                            if (isLabelsMode) {
                                return;
                            }
                            if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.INSPECT && hasGeometry) {
                                cancelDrawing();
                            }
                            setDrawMode(GAIA_DRAW_MODE.INSPECT);
                        }}
                    >
                        <InspectDrawButton />
                        <Typography variant="caption">Inspect</Typography>
                    </Box>
                )}
                <Box
                    className={`gaia-draw-control ${drawMode === GAIA_DRAW_MODE.RULER ? "selected" : ""} ${isLabelsMode ? "disabled" : ""}`}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    onClick={() => {
                        if (isLabelsMode) {
                            return;
                        }
                        if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.RULER && hasGeometry) {
                            cancelDrawing();
                        }
                        setDrawMode(GAIA_DRAW_MODE.RULER);
                    }}
                >
                    <RulerDrawButton />
                    <Typography variant="caption">Ruler</Typography>
                </Box>
                {visibleTools.map(({ Component, label, mode }, idx) => {
                    const isCurrentlyLabelsMode = isLabelsMode && mode !== GAIA_DRAW_MODE.LABELS;
                    const isLabelsTool = mode === GAIA_DRAW_MODE.LABELS;
                    const disabled = (readOnly && !isLabelsTool) || isCurrentlyLabelsMode;

                    return (
                        <Box
                            key={idx}
                            className={`gaia-draw-control ${drawMode === mode ? "selected" : ""} ${disabled ? "disabled" : ""}`}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            onClick={() => {
                                if (!disabled) {
                                    if (isLabelsMode && mode !== GAIA_DRAW_MODE.LABELS) {
                                        return;
                                    }
                                    if (drawMode !== null && drawMode !== mode && hasGeometry) {
                                        cancelDrawing();
                                    }
                                    setDrawMode(mode);
                                }
                            }}
                        >
                            <Component />
                            <Typography variant="caption">{label}</Typography>
                        </Box>
                    );
                })}
            </Box>
            <Box display="inline-flex" gap={2} padding={1}>
                {customRibbonButtons}
                {showAcceptDrawing &&
                    acceptDrawingButtons.map(({ icon, label, onClick, disabled }, idx) => (
                        <Box
                            key={`draw-action-${idx}`}
                            className={`gaia-draw-control ${disabled ? "disabled" : ""}`}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            onClick={() => {
                                if (!disabled) {
                                    onClick();
                                }
                            }}
                        >
                            <button className="gaia-draw-button" disabled={disabled}>
                                <FontAwesomeIcon icon={icon} />
                            </button>
                            <Typography variant="caption">{label}</Typography>
                        </Box>
                    ))}
                {showAcceptLabels &&
                    acceptLabelsButtons.map(({ icon, label, onClick, disabled }, idx) => (
                        <Box
                            key={`labels-action-${idx}`}
                            className={`gaia-draw-control ${disabled ? "disabled" : ""}`}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            onClick={() => {
                                if (!disabled) {
                                    onClick();
                                }
                            }}
                        >
                            <button className="gaia-draw-button" disabled={disabled}>
                                <FontAwesomeIcon icon={icon} />
                            </button>
                            <Typography variant="caption">{label}</Typography>
                        </Box>
                    ))}
                {rightButtons.map(({ icon, label, onClick, disabled, loading }, idx) => (
                    <Box
                        key={`common-action-${idx}`}
                        className={`gaia-draw-control ${disabled || loading ? "disabled" : ""}`}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        onClick={() => {
                            if (!disabled && !loading) {
                                onClick();
                            }
                        }}
                    >
                        <button className="gaia-draw-button">{loading ? <CircularProgress size="18px" /> : <FontAwesomeIcon icon={icon} />}</button>
                        <Typography variant="caption">{label}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
});
